import { onBeforeUnmount, onMounted, watch, type Ref } from 'vue'

import type { CheckoutTextField, CheckoutTexts } from '../Checkout/checkoutOverrides'

// See docs/architecture/checkout-overrides.md.

type Target = { selector: string; text: CheckoutTextField; onlyWhileShown: boolean }

const label = (fieldId: string, text: CheckoutTextField): Target => ({
  selector: `#${fieldId} > label`,
  text,
  onlyWhileShown: false,
})

const error = (wrapperId: string, text: CheckoutTextField): Target => ({
  selector: `#${wrapperId} > .PrimerCheckout__helperText`,
  text,
  onlyWhileShown: true,
})

const TARGETS: Target[] = [
  label('primer-checkout-card-number-field', 'cardNumberLabel'),
  label('primer-checkout-card-expirydate-field', 'expiryDateLabel'),
  label('primer-checkout-card-cvv-field', 'cvvLabel'),
  label('primer-checkout-cardholder-name-field', 'cardholderNameLabel'),
  error('primer-checkout-card-number', 'cardNumberErrorMessage'),
  error('primer-checkout-card-expirydate-error', 'expiryDateErrorMessage'),
  error('primer-checkout-card-cvv-error', 'cvvErrorMessage'),
  error('primer-checkout-cardholder-name-error', 'cardholderNameErrorMessage'),
]

function overwrite(el: Element, text: string, onlyWhileShown: boolean) {
  const current = el.textContent ?? ''
  if (current === text || (onlyWhileShown && !current)) return
  const only = el.childNodes.length === 1 ? el.firstChild : null
  if (only?.nodeType === Node.TEXT_NODE) only.nodeValue = text
  else el.textContent = text
}

/**
 * Keeps the author's labels and validation messages written over Primer's own,
 * in the card form rendered into `containerId`, for as long as it is mounted.
 *
 * @param containerId id of the element Primer renders into
 * @param texts the author's resolved texts
 * @param enabled false in the editor, where no Primer form renders
 */
export function usePrimerTextOverrides(
  containerId: string,
  texts: Ref<CheckoutTexts>,
  enabled: boolean,
) {
  let observer: MutationObserver | null = null
  let container: HTMLElement | null = null

  const apply = () => {
    if (!container) return
    for (const target of TARGETS) {
      const text = texts.value[target.text]
      if (!text) continue
      const el = container.querySelector(target.selector)
      if (el) overwrite(el, text, target.onlyWhileShown)
    }
  }

  onMounted(() => {
    if (!enabled || typeof MutationObserver === 'undefined') return
    container = document.getElementById(containerId)
    if (!container) return
    observer = new MutationObserver(apply)
    observer.observe(container, { childList: true, characterData: true, subtree: true })
    apply()
  })

  watch(texts, apply)

  onBeforeUnmount(() => {
    observer?.disconnect()
    observer = null
    container = null
  })
}
